/**
 * La parte alta della BI: il margine in grande con «dove vanno 100 €», poi i
 * numeri di contorno. E' il momento forte della pagina, sul fondo scuro della
 * barra (resta scuro in tutti e due i temi).
 */

import type { BiDettagli } from "../biApi";
import { calcolabili, dataBreve, eur, intero, marginePerc, n, perc, variazione } from "../biTesti";
import {
  spiegaCosti,
  spiegaFatturato,
  spiegaMargine,
  spiegaMargineMedio,
  spiegaMarginePerc,
  spiegaMedio,
  spiegaOrdini,
  spiegaPerdita,
  spiegaSostituzioni,
} from "../spiegazioni";
import { Incerto, InfoTip, type Spiegazione } from "./InfoTip";

/** Le voci della barra «ogni 100 €». L'imballo sta col prodotto (Davide
 *  02/11/2026); resi e sostituzioni sono una voce di costo come le altre. */
function vociCosto(
  d: BiDettagli,
): { chiave: string; nome: string; colore: string; valore: number }[] {
  const e = d.economia;
  const voci = [
    {
      chiave: "prodotto",
      nome: "Prodotto e imballo",
      colore: "bg-[#c96442]",
      valore: n(e.product_cost) + n(e.packaging),
    },
    { chiave: "commission", nome: "Commissioni", colore: "bg-[#9a7bb0]", valore: n(e.commission) },
    { chiave: "shipping", nome: "Spedizione", colore: "bg-[#7f9cbd]", valore: n(e.shipping) },
  ];
  if (d.costi_post_vendita) {
    voci.push({
      chiave: "post",
      nome: "Resi e sostituzioni",
      colore: "bg-[#c9a24d]",
      valore: n(d.costi_post_vendita.total),
    });
  }
  if (d.costi_bonifici) {
    voci.push({
      chiave: "bonifici",
      nome: "Bonifici fornitori",
      colore: "bg-[#6b7f5e]",
      valore: n(d.costi_bonifici.total),
    });
  }
  return voci;
}

/** Euro su 100: interi, ma sotto i 10 con un decimale (l'imballo e' 0,3 €, non 0 €). */
function su100(v: number): string {
  return `${v.toLocaleString("it-IT", { maximumFractionDigits: Math.abs(v) < 10 ? 1 : 0 })} €`;
}

function Delta({
  valore,
  prima,
  invertito = false,
}: {
  valore: number | null;
  prima: string;
  invertito?: boolean;
}) {
  if (valore == null) return null;
  const buono = invertito ? valore < 0 : valore > 0;
  const segno = valore > 0 ? "+" : valore < 0 ? "−" : "";
  return (
    <span
      title={`Rispetto a ${prima}`}
      className={`inline-flex h-[20px] items-center rounded-full px-2 text-[11.5px] font-bold tabular-nums ${
        Math.abs(valore) < 0.5
          ? "bg-paper-2 text-ink"
          : buono
            ? "bg-green-soft text-ink"
            : "bg-red-soft text-ink"
      }`}
    >
      {segno}
      {perc(Math.abs(valore), 0)}
    </span>
  );
}

export function Testata({ d }: { d: BiDettagli }) {
  const e = d.economia;
  const p = d.precedente;
  const prima = p ? `${dataBreve(p.date_from)} – ${dataBreve(p.date_to)}` : "";
  const mp = marginePerc(e);
  const base = n(e.net_revenue_computable);
  const stimato = n(e.estimate_count) > 0;
  const quota = (v: number) => (base > 0 ? (v / base) * 100 : 0);
  const VOCI = vociCosto(d);
  const resta = n(e.margin) - n(d.costi_post_vendita?.total) - n(d.costi_bonifici?.total);
  const margineQuota = quota(resta);

  return (
    <section aria-label="Margine e numeri principali" className="mb-5">
      <div className="bi-entra relative overflow-hidden rounded-[22px] bg-rail px-7 py-6 text-white shadow-[0_24px_60px_-28px_rgba(31,27,22,0.55)] max-md:rounded-[18px] max-md:px-4 max-md:py-5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-32 h-[340px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(201,100,66,0.38),transparent)] blur-2xl"
        />
        <div className="relative grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-10 max-lg:grid-cols-1 max-lg:gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[14px] font-bold text-white">Margine netto</h2>
              <InfoTip spiegazione={spiegaMargine(e, d.costi_post_vendita?.total)} />
            </div>
            <p className="mt-2 font-display text-[clamp(2.6rem,4.6vw,4.4rem)] font-bold leading-[0.95] tracking-[-0.035em] tabular-nums">
              {stimato && <span className="mr-1 opacity-70">≈</span>}
              {eur(resta)}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[14px]">
              <span className="font-display text-[22px] font-bold tabular-nums">
                {perc(d.costi_post_vendita || d.costi_bonifici ? margineQuota : mp)}
              </span>
              <span className="font-semibold">del fatturato netto</span>
              <InfoTip spiegazione={spiegaMarginePerc(e)} />
              {p && <Delta valore={variazione(n(e.margin), n(p.margin))} prima={prima} />}
            </div>
            <p className="mt-4 max-w-[46ch] text-[13px] leading-snug text-white">
              {(d.costi_post_vendita || d.costi_bonifici) && (
                <>
                  Dopo{" "}
                  {d.costi_post_vendita && d.costi_bonifici
                    ? "resi, sostituzioni e bonifici ai fornitori"
                    : d.costi_post_vendita
                      ? "resi e sostituzioni"
                      : "bonifici ai fornitori"}
                  : prima erano <b>{eur(e.margin)}</b>.{" "}
                </>
              )}
              Calcolato su <b>{intero(calcolabili(e))}</b> ordini su {intero(e.orders)}.
              {n(e.not_computable_count) > 0 && (
                <>
                  {" "}
                  <b>{intero(e.not_computable_count)}</b> senza margine (manca un costo) sono fuori.
                </>
              )}
              {p && <> Confronto con {prima}.</>}
            </p>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[14px] font-bold text-white">Ogni 100 € incassati</h2>
              <InfoTip spiegazione={spiegaCostiConto(d)} />
            </div>
            <div
              className="mt-4 flex h-[46px] w-full overflow-hidden rounded-[12px] bg-white/10"
              role="img"
              aria-label={`Su 100 euro: ${VOCI.map((v) => `${v.nome} ${Math.round(quota(v.valore))}`).join(", ")}, margine ${Math.round(margineQuota)}`}
            >
              {VOCI.map((v, i) => (
                <span
                  key={v.chiave}
                  className={`bi-cresce h-full ${v.colore}`}
                  style={{ width: `${quota(v.valore)}%`, animationDelay: `${i * 70}ms` }}
                />
              ))}
              {margineQuota > 0 && (
                <span
                  className="bi-cresce h-full bg-[#7fbf8e]"
                  style={{ width: `${margineQuota}%`, animationDelay: "280ms" }}
                />
              )}
            </div>
            <ul className="mt-4 grid grid-cols-5 gap-3 max-sm:grid-cols-3">
              {VOCI.map((v) => (
                <li key={v.chiave} className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[12px] font-semibold">
                    <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-[3px] ${v.colore}`} />
                    {v.nome}
                  </span>
                  <span className="mt-0.5 block font-display text-[22px] font-bold leading-none tabular-nums">
                    {su100(quota(v.valore))}
                  </span>
                  <span className="mt-1 block text-[11.5px] tabular-nums text-white">
                    {eur(v.valore)}
                  </span>
                </li>
              ))}
              <li className="min-w-0">
                <span className="flex items-center gap-1.5 text-[12px] font-semibold">
                  <span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px] bg-[#7fbf8e]" />
                  Ti restano
                </span>
                <span className="mt-0.5 block font-display text-[22px] font-bold leading-none tabular-nums">
                  {su100(margineQuota)}
                </span>
                <span className="mt-1 block text-[11.5px] tabular-nums text-white">
                  {eur(resta)}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-6 gap-3 max-xl:grid-cols-3 max-sm:grid-cols-2">
        <Numero
          nome="Fatturato netto"
          valore={eur(e.net_revenue)}
          spiegazione={spiegaFatturato(e)}
          delta={
            p ? (
              <Delta valore={variazione(n(e.net_revenue), n(p.net_revenue))} prima={prima} />
            ) : null
          }
          sotto={
            n(e.net_missing_count) > 0 ? (
              <Incerto stimati={0} mancanti={n(e.net_missing_count)} />
            ) : null
          }
        />
        <Numero
          nome="Ordini venduti"
          valore={intero(e.orders)}
          spiegazione={spiegaOrdini(e, d.post_vendita)}
          delta={p ? <Delta valore={variazione(n(e.orders), n(p.orders))} prima={prima} /> : null}
        />
        <Numero
          nome="Valore medio ordine"
          valore={eur(
            n(e.orders) - n(e.net_missing_count) > 0
              ? n(e.net_revenue) / (n(e.orders) - n(e.net_missing_count))
              : null,
            2,
          )}
          spiegazione={spiegaMedio(e)}
        />
        <Numero
          nome="Margine per ordine"
          valore={`${stimato ? "≈ " : ""}${eur(calcolabili(e) > 0 ? n(e.margin) / calcolabili(e) : null, 2)}`}
          spiegazione={spiegaMargineMedio(e)}
          sotto={
            <Incerto stimati={n(e.estimate_count)} mancanti={n(e.not_computable_count)} compatto />
          }
        />
        <Numero
          nome="Ordini in perdita"
          valore={intero(e.loss_count)}
          spiegazione={spiegaPerdita(e)}
          tono={n(e.loss_count) > 0 ? "rosso" : undefined}
          sotto={
            <span className="text-[12px] font-semibold tabular-nums">{eur(e.loss_amount)}</span>
          }
        />
        {d.costi_post_vendita ? (
          <Numero
            nome="Resi e sostituzioni"
            valore={eur(d.costi_post_vendita.total)}
            tono={n(d.costi_post_vendita.total) > 0 ? "rosso" : undefined}
            spiegazione={{
              titolo: "Resi e sostituzioni",
              testo:
                "Quanto ci sono costati resi e sostituzioni degli ordini del periodo: pezzi di ricambio (fatture Itamoby/Ityhome), etichette (fatture corriere), spedizioni di andata perse sui resi. Senza IVA. Il margine netto non li conta: il dettaglio e' nella scheda «Resi e sostituzioni».",
              conto: [
                {
                  etichetta: "Sostituzioni",
                  valore: eur(d.costi_post_vendita.replacements_total, 2),
                },
                {
                  segno: "+",
                  etichetta: "Resi",
                  valore: eur(d.costi_post_vendita.returns_total, 2),
                },
                { segno: "=", etichetta: "Totale", valore: eur(d.costi_post_vendita.total, 2) },
              ],
            }}
            sotto={
              <span className="text-[12px] font-semibold">
                {intero(d.costi_post_vendita.replacement_orders)} sost. ·{" "}
                {intero(d.costi_post_vendita.return_orders)} resi · margine dopo{" "}
                {eur(n(e.margin) - n(d.costi_post_vendita.total))}
              </span>
            }
          />
        ) : (
          <Numero
            nome="Sostituzioni"
            valore={eur(e.replacements_cost_net)}
            spiegazione={spiegaSostituzioni(e)}
            sotto={
              <span className="text-[12px] font-semibold">
                {intero(e.replacements_count)} pezzi rimandati
              </span>
            }
          />
        )}
      </div>
    </section>
  );
}

function spiegaCostiConto(d: BiDettagli): Spiegazione {
  const e = d.economia;
  const post = d.costi_post_vendita;
  const bonifici = d.costi_bonifici;
  const resta = n(e.margin) - n(post?.total) - n(bonifici?.total);
  const base = n(e.net_revenue_computable);
  const su100 = (v: number) =>
    base > 0 ? `${((v / base) * 100).toFixed(1).replace(".", ",")} €` : "—";
  return {
    ...spiegaCosti(),
    conto: [
      { etichetta: "Fatturato netto calcolabile", valore: eur(base) },
      {
        segno: "−",
        etichetta: `Prodotto e imballo ${eur(n(e.product_cost) + n(e.packaging))}`,
        valore: su100(n(e.product_cost) + n(e.packaging)),
      },
      { segno: "−", etichetta: `Commissioni ${eur(e.commission)}`, valore: su100(n(e.commission)) },
      { segno: "−", etichetta: `Spedizione ${eur(e.shipping)}`, valore: su100(n(e.shipping)) },
      ...(post
        ? [
            {
              segno: "−" as const,
              etichetta: `Resi e sostituzioni ${eur(post.total)}`,
              valore: su100(n(post.total)),
            },
          ]
        : []),
      ...(bonifici
        ? [
            {
              segno: "−" as const,
              etichetta: `Bonifici a Itamoby e Ityhome ${eur(bonifici.total, 2)}`,
              valore: su100(n(bonifici.total)),
            },
          ]
        : []),
      {
        segno: "=",
        etichetta: `Ti restano ${eur(resta)}`,
        valore: su100(resta),
      },
    ],
    incerto: [
      n(e.estimate_count) > 0 ? `${intero(e.estimate_count)} ordini hanno un costo stimato.` : "",
      post
        ? "Resi e sostituzioni contano su tutti gli ordini del periodo, anche quelli senza margine."
        : "",
      bonifici
        ? "I bonifici a Itamoby e Ityhome costano 0,50 € l'uno (estratto conto in Saldi), divisi fra gli ordini che pagano."
        : "",
      "Ogni voce = euro della voce ÷ fatturato netto × 100.",
    ]
      .filter(Boolean)
      .join(" "),
  };
}

function Numero({
  nome,
  valore,
  spiegazione,
  delta,
  sotto,
  tono,
}: {
  nome: string;
  valore: string;
  spiegazione: Spiegazione;
  delta?: React.ReactNode;
  sotto?: React.ReactNode;
  tono?: "rosso";
}) {
  return (
    <div className="bi-entra group rounded-[16px] border border-line bg-card px-4 py-3.5 transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:border-line-2 hover:shadow-[0_10px_24px_-16px_rgba(31,27,22,0.35)]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-bold">{nome}</span>
        <InfoTip spiegazione={spiegazione} />
      </div>
      <p
        className={`mt-1.5 font-display text-[26px] font-bold leading-none tracking-[-0.02em] tabular-nums max-md:text-[22px] ${
          tono === "rosso" ? "text-red" : ""
        }`}
      >
        {valore}
      </p>
      <div className="mt-2 flex min-h-[20px] flex-wrap items-center gap-1.5">
        {delta}
        {sotto}
      </div>
    </div>
  );
}
